import { useState } from 'react'
import { BaseOrgLimitCard, type LimitField } from '../components'
import {
  useGetOrgSpendingLimit,
  useCreateOrgSpendingLimit,
  useCreateSpendingLimitAutomation,
  useListSpendingLimitAutomations,
  useDeleteSpendingLimitAutomation,
} from '@/services/settings'
import { z } from 'zod'
import { centsToDollars, dollarsToCents } from '@/utils/money'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, Calendar, DollarSign } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-hot-toast'
import { WebrpcError } from '@/services/inference.gen'

type OrgCreditLimitCardProps = {
  onLimitChange: (limit: number) => void
}

const creditLimitFormSchema = z.object({
  creditLimit: z.number().min(0, 'Credit limit cannot be negative'),
})

const SCHEDULE_PRESETS = {
  daily: { label: 'Daily', cron: '0 0 * * *' },
  weekly: { label: 'Weekly (Sundays)', cron: '0 0 * * 0' },
  monthly: { label: 'Monthly (1st day)', cron: '0 0 1 * *' },
  custom: { label: 'Custom Schedule', cron: '' },
} as const

const automationFormSchema = z.object({
  amount: z.number().min(0.01, 'Amount must be at least $0.01'),
  schedule: z.enum(['daily', 'weekly', 'monthly', 'custom']),
  customCron: z.string().optional(),
  maxExecutions: z.number().optional(),
})

export const OrgCreditLimitCard = ({
  onLimitChange,
}: OrgCreditLimitCardProps) => {
  const [showAutomationDialog, setShowAutomationDialog] = useState(false)

  const { data: orgSpendingLimit, isLoading } = useGetOrgSpendingLimit()

  const { data: automations, isLoading: isAutomationsLoading } =
    useListSpendingLimitAutomations(orgSpendingLimit?.id || null)

  const createAutomationMutation = useCreateSpendingLimitAutomation()
  const deleteAutomationMutation = useDeleteSpendingLimitAutomation()

  const automationForm = useForm<z.infer<typeof automationFormSchema>>({
    resolver: zodResolver(automationFormSchema),
    defaultValues: {
      amount: 100,
      schedule: 'monthly',
    },
  })

  const currentLimits = {
    creditLimit: orgSpendingLimit
      ? centsToDollars(orgSpendingLimit.max_spend_cent)
      : 0,
  }

  const limitFields: LimitField[] = [
    {
      name: 'creditLimit',
      label: 'New Credit Limit',
      unit: 'per user',
      step: 10,
      minValue: 100,
      testId: 'credit-limit-input',
    },
  ]

  const updateMutation = useCreateOrgSpendingLimit()

  const confirmationConfig = {
    title: 'Confirm Organization-Wide Update',
    description: (
      watchedValues: { creditLimit: number },
      currentLimits: { creditLimit: number }
    ) =>
      `You're about to change the credit limit from $${currentLimits.creditLimit} to $${watchedValues.creditLimit} for all users.`,
    confirmText: (watchedValues: { creditLimit: number }) =>
      watchedValues.creditLimit.toString(),
    confirmPrompt: (watchedValues: { creditLimit: number }) =>
      `Please type "${watchedValues.creditLimit}" to confirm:`,
    warningPoints: [
      'All customized user limits will be overwritten',
      'Some running resources may be affected if the limit is lowered',
    ],
  }

  const testIds = {
    editButton: 'edit-org-credit-limit-button',
    cancelButton: 'cancel-credit-limit-button',
    applyButton: 'apply-credit-limit-button',
  }

  const wrappedUpdateMutation = {
    mutate: (
      data: { creditLimit: number },
      options?: { onSuccess?: () => void; onError?: (error: any) => void }
    ) => {
      updateMutation.mutate({ cent: dollarsToCents(data.creditLimit) }, options)
    },
    isPending: updateMutation.isPending,
  }

  const onSubmitAutomation = (values: z.infer<typeof automationFormSchema>) => {
    if (!orgSpendingLimit || !orgSpendingLimit.id) {
      toast.error('Please create an organization spending limit first')
      return
    }

    const crontab =
      values.schedule === 'custom'
        ? values.customCron || '0 0 * * *'
        : SCHEDULE_PRESETS[values.schedule].cron

    createAutomationMutation.mutate(
      {
        spending_limit_id: orgSpendingLimit.id,
        cent: dollarsToCents(values.amount),
        crontab,
        enable: true,
        max_term: values.maxExecutions,
      },
      {
        onSuccess: () => {
          setShowAutomationDialog(false)
          automationForm.reset()
          toast.success('Auto-increase scheduled successfully')
        },
        onError: (error) => {
          if (error instanceof WebrpcError) {
            toast.error(JSON.stringify(error))
          } else {
            toast.error('Failed to update credit limit')
          }
        },
      }
    )
  }

  const handleDeleteAutomation = (autoId: number) => {
    deleteAutomationMutation.mutate(autoId)
  }

  const getCronDescription = (crontab: string) => {
    if (crontab === '0 0 * * *') return 'Daily'
    if (crontab === '0 0 * * 0') return 'Weekly'
    if (crontab === '0 0 1 * *') return 'Monthly'
    return crontab
  }

  return (
    <>
      <BaseOrgLimitCard
        isLoading={isLoading}
        currentLimits={currentLimits}
        limitFields={limitFields}
        formSchema={creditLimitFormSchema}
        onLimitChange={onLimitChange}
        updateMutation={wrappedUpdateMutation}
        confirmationConfig={confirmationConfig}
        editButtonLabel="Edit Organization limit"
        testIds={testIds}
      />

      {orgSpendingLimit && !orgSpendingLimit.id && (
        <div className="mt-4 p-4 border rounded-lg bg-muted/50">
          <p className="text-sm text-muted-foreground">
            Create an organization spending limit first to enable auto-increase
            functionality.
          </p>
        </div>
      )}

      {orgSpendingLimit && !!orgSpendingLimit.id && (
        <div className="mt-4 p-4 border rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-sm font-medium">Auto-Increase</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Schedule automatic limit increases
              </p>
            </div>
            <div className="flex items-center gap-2">
              {automations && automations.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  Limited to 1 automation
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAutomationDialog(true)}
                disabled={automations && automations.length > 0}
              >
                <Plus className="h-3 w-3 mr-1" /> Setup
              </Button>
            </div>
          </div>

          {isAutomationsLoading ? (
            <div className="text-sm text-muted-foreground">
              Loading automations...
            </div>
          ) : automations && automations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Amount</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {automations.map((automation) => (
                  <TableRow key={automation.id}>
                    <TableCell>
                      <div className="flex items-center">
                        <DollarSign className="h-3 w-3 mr-1 text-muted-foreground" />
                        {centsToDollars(automation.cent)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1 text-muted-foreground" />
                        {getCronDescription(automation.crontab)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {automation.max_term ? (
                        <span className="text-sm">
                          {automation.now_term} / {automation.max_term}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Unlimited
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={automation.enabled ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {automation.enabled ? 'Active' : 'Paused'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleDeleteAutomation(automation.id)}
                        disabled={deleteAutomationMutation.isPending}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-2">
              No automations configured
            </div>
          )}
        </div>
      )}

      <Dialog
        open={showAutomationDialog}
        onOpenChange={setShowAutomationDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Up Auto-Increase</DialogTitle>
            <DialogDescription>
              Schedule automatic increases to the organization credit limit
            </DialogDescription>
          </DialogHeader>

          <Form {...automationForm}>
            <form
              onSubmit={automationForm.handleSubmit(onSubmitAutomation)}
              className="space-y-4"
            >
              <FormField
                control={automationForm.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Increase Amount ($)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription>
                      Amount to add to the limit each time
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={automationForm.control}
                name="schedule"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Schedule</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(SCHEDULE_PRESETS).map(
                          ([key, preset]) => (
                            <SelectItem key={key} value={key}>
                              {preset.label}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      How often to increase the limit
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {automationForm.watch('schedule') === 'custom' && (
                <FormField
                  control={automationForm.control}
                  name="customCron"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Custom Cron Expression</FormLabel>
                      <FormControl>
                        <Input placeholder="0 0 * * *" {...field} />
                      </FormControl>
                      <FormDescription>
                        <div className="space-y-2">
                          <div className="text-xs space-y-1">
                            <div className="font-semibold">
                              Standard 5-field format only. Quartz/7-field
                              format is not supported.
                            </div>
                            <div className="font-mono">
                              Format: [minute hour day month weekday]
                            </div>
                            <div className="font-mono text-muted-foreground mt-2">
                              Examples:
                              <div>• */15 * * * * (every 15 minutes)</div>
                              <div>• 0 */6 * * * (every 6 hours)</div>
                              <div>• 0 9 * * 1-5 (9 AM on weekdays)</div>
                              <div>• 0 0 */3 * * (every 3 days)</div>
                            </div>
                          </div>
                          <div className="text-xs">
                            Need help? Use{' '}
                            <a
                              href="https://crontab.cronhub.io/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline"
                            >
                              crontab.cronhub.io
                            </a>{' '}
                            for standard cron expressions
                          </div>
                        </div>
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={automationForm.control}
                name="maxExecutions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum Executions (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : undefined
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Leave empty for unlimited increases
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAutomationDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createAutomationMutation.isPending}
                >
                  {createAutomationMutation.isPending
                    ? 'Creating...'
                    : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  )
}
